/* eslint-disable complexity */
// 9fbef606107a605d69c0edbcd8029e5d
import { call, takeLatest, put, select } from 'redux-saga/effects';
import { validateReduxCache } from '@tcp/core/src/utils/cache.util';
import logger from '@tcp/core/src/utils/loggerInstance';
import { setClickAnalyticsData, trackClick } from '@tcp/core/src/analytics/actions';
import {
  getLocationStores,
  setFavoriteStore,
  getCurrentStoreInfoApi,
} from '@tcp/core/src/services/abstractors/common/storeLocator';
import {
  setDefaultStore as setDefaultStoreUserAction,
  getUserInfo,
} from '@tcp/core/src/components/features/account/User/container/User.actions';
import {
  getIsGenericUser,
  getDefaultStore,
  getUserId,
} from '@tcp/core/src/components/features/account/User/container/User.selectors';
import { getAPIConfig, getDateObject, updateStoreDataLocalStorage } from '@tcp/core/src/utils';
import {
  getFetchClosestStore,
  getStoreLocatorMapboxTileIDs,
  getSwitchToSfccForStoreInfo,
  getBossDefaultRefreshTime,
} from '@tcp/core/src/reduxStore/selectors/session.selectors';
import { getSuggestedStores } from './StoreLanding.selectors';
import STORE_LOCATOR_CONSTANTS from './StoreLanding.constants';
import { setStoresByCoordinates, updateStoreSearchStatus } from './StoreLanding.actions';
import names from '../../../../../constants/eventsName.constants';
import { getLocalStorage } from '../../../../../utils/localStorageManagement';
import tokenAbstractor from '../../../../../services/abstractors/bootstrap/token';
import { getESTDateTimeFromDate } from '../../../../../utils/utils';

export function* getMapboxTileIDs() {
  return yield select(getStoreLocatorMapboxTileIDs);
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function* fetchLocationStoresSaga({ payload }) {
  try {
    let res;
    const payloadCopy = { ...payload };
    const FETCH_CLOSEST_STORE = yield select(getFetchClosestStore);
    const SWITCH_TO_SFCC_FOR_STORE_INFO = yield select(getSwitchToSfccForStoreInfo);
    const storeLocatorSwitches = {
      FETCH_CLOSEST_STORE,
      SWITCH_TO_SFCC_FOR_STORE_INFO,
    };
    const currentDate = getDateObject();
    const bossRefreshTime = yield select(getBossDefaultRefreshTime);
    const bossDefaultRefreshTime = bossRefreshTime || '04:00:00';

    const serverTime = yield call(tokenAbstractor.getServerTime);
    const serverDate = serverTime ? getESTDateTimeFromDate(new Date(serverTime * 1000)) : null;

    const getRefreshTimeConfig = (date) => {
      const isoDate = date.toISOString().split('T')[0];
      const refresh = new Date(`${isoDate}T${bossDefaultRefreshTime}+05:30`);
      return getESTDateTimeFromDate(refresh);
    };
    const refreshTimeConfig = getRefreshTimeConfig(currentDate);

    if (payloadCopy?.akamaiBasedLogic) {
      const mapBoxTileId = yield call(getMapboxTileIDs);
      payloadCopy.storeLocatorMapboxTileIDs = mapBoxTileId;
    } else {
      yield put(updateStoreSearchStatus({ storeSearchInProgress: true }));
      res = yield call(getLocationStores, payloadCopy, storeLocatorSwitches); // for non akamai store just calling Api directly
    }
    const defaultStore = JSON.parse(getLocalStorage('defaultStore'));

    const timeStampCheck = defaultStore?.akamaiStoreTimeStamp || defaultStore?.favStoreTimeStamp;

    const shouldInvalidate =
      serverDate &&
      timeStampCheck &&
      getESTDateTimeFromDate(new Date(timeStampCheck)).getTime() <= refreshTimeConfig.getTime() &&
      serverDate.getTime() >= refreshTimeConfig.getTime();

    if (defaultStore && !timeStampCheck) {
      // if no timestamp in locastorage store we add the fav store timestamp
      defaultStore.favStoreTimeStamp = currentDate;
      updateStoreDataLocalStorage(defaultStore);
    }
    if (!defaultStore && payload?.akamaiBasedLogic) {
      res = yield call(getLocationStores, payloadCopy, storeLocatorSwitches);
      if (res && res.length > 0) {
        const defaultStoreStorage = res?.[0];
        defaultStoreStorage.akamaiStoreTimeStamp = currentDate;
        defaultStoreStorage.akamaiStoreFlag = true;
        updateStoreDataLocalStorage(defaultStoreStorage);
        yield put(setDefaultStoreUserAction(defaultStoreStorage));
      }
    } else if (defaultStore && shouldInvalidate) {
      res = yield call(getLocationStores, payloadCopy, storeLocatorSwitches);
      if (res && res.length > 0) {
        const defaultStoreStorage = res?.[0];
        if (defaultStore?.akamaiStoreTimeStamp) {
          defaultStoreStorage.akamaiStoreTimeStamp = currentDate;
        } else if (defaultStore?.favStoreTimeStamp) {
          defaultStoreStorage.favStoreTimeStamp = currentDate;
        }
        yield put(setDefaultStoreUserAction(defaultStoreStorage));
        updateStoreDataLocalStorage(defaultStoreStorage);
      }
    } else if (defaultStore) {
      yield put(setDefaultStoreUserAction(defaultStore));
    }

    if (res && Array.isArray(res)) {
      yield put(setStoresByCoordinates(res));
    } else if (!payload.akamaiBasedLogic) {
      yield put(
        setClickAnalyticsData({
          customEvents: ['event89', 'event90'],
          storeSearchCriteria: payload.radius,
          clickEvent: true,
        })
      );
      yield put(
        trackClick({
          name: names.screenNames.search_store_null,
        })
      );
      throw res;
    }
  } catch (err) {
    yield put(setStoresByCoordinates([]));
  }
}

function checkFavoriteStoreNullSetCondition(isForceUpdate, isGenericUser, lat, long, storeId) {
  return !isForceUpdate && isGenericUser && lat === null && long === null && !storeId;
}

/**
 * @method getFavoriteStoreSagaError
 * @param {object} err - error object from the saga call
 */
export function* getFavoriteStoreSagaError() {
  return yield put(setDefaultStoreUserAction(null));
}

/**
 * @method getFavoriteStoreSaga
 * @description - Calls favorite store wcs api or graphql store/info
 * @param {object} payload consisting of store info
 */
export function* getFavoriteStoreSaga({ payload }) {
  try {
    const {
      geoLatLang: { lat = null, long = null } = {},
      isForceUpdate = false,
      storeId,
    } = payload;
    const isGenericUser = yield select(getIsGenericUser);
    if (checkFavoriteStoreNullSetCondition(isForceUpdate, isGenericUser, lat, long, storeId)) {
      return yield put(setDefaultStoreUserAction(null));
    }
    const apiConfig = getAPIConfig();
    let response = null;
    const getFavDefaultStore = yield select(getDefaultStore);
    if (!isGenericUser || isForceUpdate) {
      const { brandIdCMS } = getAPIConfig();
      // Country check is not required for store search hence sending empty string.
      const country = '';
      if (getFavDefaultStore) {
        response = yield call(
          getCurrentStoreInfoApi.getData,
          brandIdCMS,
          country,
          getFavDefaultStore,
          apiConfig
        );
      }
    }
    const validResponse = response && response.basicInfo ? response : null;
    if (storeId && validResponse) {
      updateStoreDataLocalStorage(validResponse);
    }
    return yield put(setDefaultStoreUserAction(validResponse));
  } catch (err) {
    return yield call(getFavoriteStoreSagaError);
  }
}

export function* setFavoriteStoreSaga({ payload }) {
  try {
    const state = yield select();
    const userId = yield select(getUserId);
    let suggestedStore = payload;
    const favStoreId = payload && payload.basicInfo ? payload.basicInfo.id : '';
    if (payload.key === STORE_LOCATOR_CONSTANTS.STORE_LOCATOR) {
      suggestedStore = yield getSuggestedStores(state, favStoreId);
    }
    const res = favStoreId
      ? yield call(setFavoriteStore, favStoreId, state, suggestedStore, userId)
      : {};
    if (res) {
      yield put(setDefaultStoreUserAction(payload));
      yield put(getUserInfo({ ignoreCache: true, loadFavStore: true }));
    }
  } catch (err) {
    logger.error(err);
  }
}

export function* StoreLocatorSaga() {
  const cachedFavoriteStore = validateReduxCache(getFavoriteStoreSaga);
  yield takeLatest(STORE_LOCATOR_CONSTANTS.GET_LOCATION_STORES, fetchLocationStoresSaga);
  yield takeLatest(STORE_LOCATOR_CONSTANTS.GET_FAVORITE_STORE, cachedFavoriteStore);
  yield takeLatest(STORE_LOCATOR_CONSTANTS.SET_FAVORITE_STORE, setFavoriteStoreSaga);
}

export default StoreLocatorSaga;
